import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Btn, Field, Textarea, Notice } from "./kit";
import { useOnline } from "../lib/draft";
import { fmtDateTime } from "../lib/format";

// Outcome-named confirmation: states recipient/consequence; optional required reason.
export function ConfirmDialog({ open, onOpenChange, title, consequence, recipient, confirmLabel, onConfirm, reasonLabel, reasonRequired, testid = "confirm-dialog", children, danger }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const go = async () => {
    if (reasonRequired && !reason.trim()) return setError(t("err.required"));
    setBusy(true);
    try {
      await onConfirm(reason);
      setReason("");
      setError(null);
      onOpenChange(false);
    } catch (e) {
      setError(e?.message || t("err.unknown"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid} className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{title}</DialogTitle>
          {consequence && <DialogDescription className="text-[15px] text-foreground/80">{consequence}</DialogDescription>}
        </DialogHeader>
        {recipient && <p className="text-sm"><span className="font-semibold">{t("confirm.recipient")}:</span> {recipient}</p>}
        {children}
        {reasonLabel && (
          <Field label={reasonLabel} required={reasonRequired} error={error} htmlFor="confirm-reason">
            <Textarea id="confirm-reason" data-testid={`${testid}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        )}
        {!reasonLabel && error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Btn variant="ghost" onClick={() => onOpenChange(false)} testid={`${testid}-cancel`}>{t("common.back")}</Btn>
          <Btn variant={danger ? "danger" : "primary"} disabled={busy} onClick={go} testid={`${testid}-confirm`}>{confirmLabel}</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DraftBanner({ pending, restored, dirty }) {
  const { t } = useTranslation();
  const online = useOnline();
  if (!online && dirty) return <Notice tone="offline" testid="draft-saved-locally">{t("offline.saved_local")}</Notice>;
  if (pending) return <Notice tone="offline" testid="draft-saved-locally">{t("offline.saved_local")} {online && t("offline.back_online")}</Notice>;
  if (restored && dirty) return <Notice testid="draft-restored">{t("offline.restored")}</Notice>;
  return null;
}

export function HistoryList({ items, testid = "history-list" }) {
  const { t } = useTranslation();
  if (!items?.length) return <p className="text-sm text-muted-foreground">{t("history.empty")}</p>;
  return (
    <ol data-testid={testid} className="space-y-2">
      {items.map((h) => (
        <li key={h.id} className="rounded-xl border bg-white p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold">{t(`action.${h.action}`, { defaultValue: h.action })}</span>
            <span className="text-xs text-muted-foreground">{fmtDateTime(h.at)}</span>
          </div>
          <p className="text-muted-foreground">{h.actor_name}</p>
          {h.reason && <p><span className="font-semibold">{t("common.reason")}:</span> {h.reason}</p>}
          {(h.old || h.new) && (
            <details className="mt-1">
              <summary className="cursor-pointer text-xs font-semibold text-[hsl(268_32%_34%)]">{t("history.values")}</summary>
              {h.old && <pre className="mt-1 whitespace-pre-wrap break-words rounded-lg bg-muted p-2 text-xs"><b>{t("history.old")}:</b> {JSON.stringify(h.old, null, 1)}</pre>}
              {h.new && <pre className="mt-1 whitespace-pre-wrap break-words rounded-lg bg-[hsl(var(--sage))] p-2 text-xs"><b>{t("history.new")}:</b> {JSON.stringify(h.new, null, 1)}</pre>}
            </details>
          )}
        </li>
      ))}
    </ol>
  );
}

export function NoAccess() {
  const { t } = useTranslation();
  return (
    <div data-testid="no-access" className="mx-auto max-w-md rounded-2xl border bg-white p-8 text-center fade-up">
      <h1 className="text-2xl font-extrabold">{t("noaccess.title")}</h1>
      <p className="mt-2 text-muted-foreground">{t("noaccess.body")}</p>
      <p data-testid="no-access-contact" className="mt-4 inline-flex rounded-xl bg-[hsl(var(--peach))] px-4 py-3 font-semibold text-[hsl(14_56%_28%)]">{t("noaccess.contact")}</p>
    </div>
  );
}

export function Loading() {
  const { t } = useTranslation();
  return <div data-testid="loading" className="py-16 text-center text-muted-foreground">{t("common.loading")}</div>;
}
